import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { PRODUCTS } from "@/data/products";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { LanguageSelector, CurrencySelector } from "@/components/LangCurrencySelectors";
import GlobalSearch from "@/components/GlobalSearch";
import SbcLogo from "@/components/layout/SbcLogo";

// Feature flag — toggle the Scitygate Group top-bar (Telecom / Brewing / Trading / Lettings / Institute).
// Set REACT_APP_SHOW_GROUP_BAR=true in frontend/.env once those group companies are ready to launch.
// Kept as a runtime read so no rebuild is needed to flip it in the deployed environment.
const SHOW_GROUP_BAR = String(process.env.REACT_APP_SHOW_GROUP_BAR || "").toLowerCase() === "true";

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  const NAV_LINKS = [
    { to: "/about", label: t("nav.about") },
    { to: "/export-africa", label: t("nav.export") },
    { to: "/sustainability", label: t("nav.sustainability") },
    { to: "/quality", label: t("nav.quality") },
    { to: "/news", label: t("nav.news") },
    { to: "/careers", label: t("nav.careers") },
    { to: "/investors", label: t("nav.investors") },
  ];

  return (
    <header className="sticky top-0 z-50 glass-nav" data-testid="site-navbar">
      {SHOW_GROUP_BAR && (
        <div className="hidden md:flex justify-between items-center bg-neutral-950 text-neutral-300 text-[11px] tracking-wider px-6 py-1.5" data-testid="scitygate-group-bar">
          <span className="font-medium">{t("nav.scitygate_group")}</span>
          <nav className="flex gap-5">
            <a href="https://telecom.scitygate.com" className="hover:text-gold-400 transition-colors">Telecom</a>
            <a href="https://brewing.scitygate.com" className="text-gold-400">Brewing</a>
            <a href="https://trading.scitygate.com" className="hover:text-gold-400 transition-colors">Trading</a>
            <a href="https://lettings.scitygate.com" className="hover:text-gold-400 transition-colors">Lettings</a>
            <a href="https://institute.scitygate.com" className="hover:text-gold-400 transition-colors">Institute</a>
          </nav>
          <a href="mailto:info@scitygate.com" className="hover:text-gold-400 transition-colors">info@scitygate.com</a>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <SbcLogo className="h-14 sm:h-16 w-auto group-hover:scale-105 transition-transform duration-300" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
            <Link to="/brands" className="flex items-center gap-1 px-3 py-2 text-sm font-medium hover:text-gold-500 transition-colors" data-testid="nav-brands">
              {t("nav.brands")} <ChevronDown className="w-3.5 h-3.5" />
            </Link>
            {productsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[720px]" data-testid="brands-mega-menu">
                <div className="bg-background border border-border rounded-2xl shadow-2xl p-5">
                  <div className="grid grid-cols-2 gap-2">
                    {PRODUCTS.map((p) => (
                      <div key={p.slug} className="group rounded-xl p-3 hover:bg-muted/60 transition-colors" data-testid={`nav-brand-${p.slug}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white font-display font-black text-lg flex-none" style={{ background: p.accent }}>
                            {p.name.slice(0, 2)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-0.5">
                              <div className="font-display font-bold text-base group-hover:text-gold-500 transition-colors truncate" style={{ color: p.accent }}>
                                {p.name.toUpperCase()}
                              </div>
                              <sup className="text-[10px] font-bold" style={{ color: p.accent }}>®</sup>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{p.tagline}</div>
                            <Link to={`/brands/${p.slug}`}
                                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest"
                                  style={{ color: p.accent }}>
                              Explore <ChevronDown className="w-3 h-3 -rotate-90" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/brands" className="mt-4 block text-center text-xs font-semibold uppercase tracking-widest text-gold-500 hover:text-gold-400 border-t border-border pt-4">
                    View all brands →
                  </Link>
                </div>
              </div>
            )}
          </div>
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-gold-500" : "hover:text-gold-500"}`} data-testid={`nav-${l.to.replace(/\W+/g,'')}`}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/contact" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-gold-500" : "hover:text-gold-500"}`} data-testid="nav-contact">{t("nav.contact")}</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <a href="mailto:info@scitygate.com" className="hidden xl:inline-block text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 hover:text-gold-500 tracking-wide mr-1" data-testid="nav-email">info@scitygate.com</a>
          <GlobalSearch />
          <LanguageSelector />
          <CurrencySelector />
          <button onClick={toggle} className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors" aria-label="Toggle theme" data-testid="theme-toggle">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link to="/distributor" className="hidden xl:block" data-testid="nav-distributor-cta">
            <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full text-xs px-5 h-9 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-100">{t("nav.distributor_cta")}</Button>
          </Link>
          <button className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-border" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground py-2">{t("nav.brands")}</div>
            {PRODUCTS.map((p) => (
              <Link key={p.slug} to={`/brands/${p.slug}`} onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">{p.name}</Link>
            ))}
            <div className="h-px bg-border my-2" />
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">{l.label}</Link>
            ))}
            <Link to="/contact" onClick={() => setOpen(false)} className="block py-2 text-sm font-medium">{t("nav.contact")}</Link>
            <Link to="/distributor" onClick={() => setOpen(false)} className="block py-2 text-sm font-semibold text-gold-500">{t("nav.distributor_cta")} →</Link>
            <div className="h-px bg-border my-2" />
            <Link to="/portal/distributor" onClick={() => setOpen(false)} className="block py-2 text-xs uppercase tracking-wider">{t("nav.distributor_portal")}</Link>
            <Link to="/portal/investor" onClick={() => setOpen(false)} className="block py-2 text-xs uppercase tracking-wider">{t("nav.investor_portal")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
